import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IBroadCastChannel } from "@/types/IBroadcastChannel";
import { IBroadcastFeed } from "@/types/IBroadcastFeed";
import { IBroadCastChannelFeed } from "@/types/IBroadCastChannelFeed";

type ITrench = {
  trench: IBroadCastChannel;
  trenchDetail: IBroadcastFeed[];
};

const initialState: ITrench[] = [];

export const TrenchesSlice = createSlice({
  name: "trenches_slice",
  initialState,
  reducers: {
    setMyTrenchesInRedux: (
      state,
      action: PayloadAction<IBroadCastChannel[]>
    ) => {
      const uniqueTrenchIds = new Set(
        action.payload.map((trench) => trench.id)
      );

      const newTrenches = action.payload.map((trench) => ({
        trench,
        trenchDetail: [],
      }));

      // Commented this out because it returns all the previous trenches

      // Filter out existing trenches with duplicate IDs and concatenate the new array on top
      // const newState: ITrench[] = state
      //   .filter((trench) => !uniqueTrenchIds.has(trench.trench.id))
      //   .concat(newTrenches);

      return newTrenches;
    },
    addTrenchInRedux: (state, action: PayloadAction<IBroadCastChannel>) => {
      const index = state.findIndex((x) => x.trench.id === action.payload.id);
      if (index === -1) {
        state.unshift({
          trench: action.payload,
          trenchDetail: [],
        });
      }
    },
    createNewPostInTrenchInRedux: (
      state,
      action: PayloadAction<{ trenchId: number; feed: IBroadcastFeed }>
    ) => {
      const { trenchId, feed } = action.payload;
      const index = state.findIndex((x) => x.trench.id === trenchId);
      if (index !== -1) {
        // Add the new post to the trench's trenchDetail
        state[index].trenchDetail.push(feed);
      }
    },

    setTrenchesFeedInRedux: (
      state,
      action: PayloadAction<{ trenchId: number; feed: IBroadcastFeed[] }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        // Append the new feed array on top of existing trenchDetail
        state[index].trenchDetail = [
          ...action.payload.feed,
          ...state[index].trenchDetail,
        ];
      }
    },
    editTrenchesFeedInRedux: (
      state,
      action: PayloadAction<{
        trenchId: number;
        editObj: IBroadcastFeed;
        textContent: string;
      }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        // Create a copy of the trenchDetail array
        const trenchMessages = [...state[index].trenchDetail];
        const jIndex = trenchMessages.findIndex(
          (x) => x.id === action.payload.editObj.id
        );
        if (jIndex !== -1) {
          // Create a new feed object with updated textContent
          const updatedFeed = {
            ...action.payload.editObj,
          };
          // Update the trenchDetail array with the updated feed object
          trenchMessages[jIndex] = updatedFeed;
          state[index].trenchDetail = trenchMessages;
        }
      }
    },
    // find all the feed of specific trench by id and then find the feed by id and update the feed and update isPinned field
    pinMsgInTrenchFeedInRedux: (
      state,
      action: PayloadAction<{
        trenchId: number;
        feedId: number;
      }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        // Create a copy of the trenchDetail array
        const trenchMessages = [...state[index].trenchDetail];
        // check all the message of trenchMessages and if find isPinned to 1 make it 0
        trenchMessages.forEach((msg) => {
          if (msg.isPinned === 1) {
            msg.isPinned = 0;
          }
        });
        const jIndex = trenchMessages.findIndex(
          (x) => x.id === action.payload.feedId
        );
        if (jIndex !== -1) {
          // Create a new feed object with updated isPinned field
          const updatedFeed = {
            ...trenchMessages[jIndex],
            isPinned: 1,
          };
          // Update the trenchDetail array with the updated feed object
          trenchMessages[jIndex] = updatedFeed;
          state[index].trenchDetail = trenchMessages;
        } else {
          state[index].trenchDetail = trenchMessages;
        }
      }
    },
    editTrenchInRedux: (state, action: PayloadAction<{ trench: any }>) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trench.id
      );
      if (index !== -1) {
        let temp = { ...state[index].trench };
        state[index].trench = { ...temp, ...action.payload.trench };
      }
    },
    deleteFeedFromTrenchInRedux: (
      state,
      action: PayloadAction<{ trenchId: number; feedId: number }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        // Create a copy of the trenchDetail array
        const trenchMessages = [...state[index].trenchDetail];
        const feedIndex = trenchMessages.findIndex(
          (x) => x.id === action.payload.feedId
        );
        if (feedIndex !== -1) {
          // Remove the feed object from trenchDetail array
          trenchMessages.splice(feedIndex, 1);
          // Update the trenchDetail array in state
          state[index].trenchDetail = trenchMessages;
        }
      }
    },
    deleteTrenchByIdInRedux: (
      state,
      action: PayloadAction<{ trenchId: number }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        state.splice(index, 1);
      }
    },
    readRedDotNotificationInRedux: (
      state,
      action: PayloadAction<{ trenchId: number }>
    ) => {
      const index = state.findIndex(
        (x) => x.trench.id === action.payload.trenchId
      );
      if (index !== -1) {
        // Create a copy of the trench object
        const updatedTrench = {
          ...state[index],
          trench: {
            ...state[index].trench,
            amountUnreadMessages: 0,
          },
        };
        // Update the state with the new trench object
        state[index] = updatedTrench;
      }
    },
  },
});

export const {
  setMyTrenchesInRedux,
  createNewPostInTrenchInRedux,
  addTrenchInRedux,
  setTrenchesFeedInRedux,
  editTrenchesFeedInRedux,
  editTrenchInRedux,
  deleteFeedFromTrenchInRedux,
  deleteTrenchByIdInRedux,
  readRedDotNotificationInRedux,
  pinMsgInTrenchFeedInRedux,
} = TrenchesSlice.actions;

export default TrenchesSlice.reducer;
